<script lang="ts">
    import type {FormControl} from '../../internal/forms/form-control';
    import {FormControlBuilder} from '../../internal/forms/form-control';
    import {formControls} from '../../internal/forms/use.form-controls';
    import type {ValueEditorSize} from '../../types';
    import {style} from 'svelte-forms';
    import {createEventDispatcher, onDestroy, onMount, tick} from 'svelte';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import {focusOnMount} from '../../internal/use.focus-on-mount';
    import {touched} from '../../internal/use.touched';
    import {barcodeScanner} from './use.barcode-scanner';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import type {FormControlOptions} from '../../internal/forms/types';
    import type {TextValueEditorValidations} from '../text/types';
    import {validatorFactory} from '../text/utils';
    import type {ScannableTextValueEditorLocalizations, ScannableTextValueEditorOptions} from './types';
    import {emptyAsNullFormatter, emptyAsNullParser, trim} from '../_shared/editors-utils';
    import BarcodeScannerInputControl from './barcode-scanner-input-control/BarcodeScannerInputControl.svelte';
    import type {Injector} from '../../../../svelte/types';
    import {getContext} from 'svelte';
    import {INJECTOR_KEY} from '../../../../svelte/utils';
    import {BarcodeScannerService} from '../../../../barcode-scanner/barcode-scanner.service';
    import {UserFeedbackService} from '../../../services/user-feedback.service';
    import {INPUT_GROUP_HIDDEN_CLASS} from './utils';
    import Description from '../../description/Description.svelte';
    import {createInputDescription} from '../../description/utils';
    import {EDITORS_LOCALIZE} from '../../context-keys';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import type {EditorsLocalizationFunction} from '../../localizations/types';
    import type {FormSubmitRequestEvent} from '../../events';
    import {firstValueFrom} from 'rxjs';
    import {required} from '../../internal/forms/use.required';
    import {getLogger} from '../../../../svelte/context';

    export let model: string;
    export let formControlOptions: FormControlOptions;
    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;
    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;
    export let options: ScannableTextValueEditorOptions;
    export let validations: TextValueEditorValidations;

    const localize: EditorsLocalizationFunction<ScannableTextValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const dispatch = createEventDispatcher<{'form-submit-request': FormSubmitRequestEvent['detail']}>();
    const injector: Injector = getContext(INJECTOR_KEY);
    const barcodeScannerService: BarcodeScannerService = injector.getByToken(BarcodeScannerService.serviceName);
    const userFeedbackService: UserFeedbackService = injector.getByToken(UserFeedbackService.serviceName);


    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations, 'text'))
        .withOptions(formControlOptions)
        .withModifiers([trim(options), emptyAsNullParser(options)])
        .withFormatters([emptyAsNullFormatter(options)])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    let isScanningSubsystemInitialized = false;
    let inputElement: HTMLInputElement;
    let inputGroupElement;

    onMount(() => tick().then(() => isScanningSubsystemInitialized = true)); // to ensure that input will focus after the system is ready

    onDestroy(() => subscription.unsubscribe());

    async function onScan() {
        if (options.autosubmit) {
            await firstValueFrom(formControl.getModelInputChangeEvent$()); // wait until scanned value gets in model
            dispatch('form-submit-request', {submitter: inputElement});
        }
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

<template>
    <div class="{INPUT_GROUP_HIDDEN_CLASS}" bind:this={inputGroupElement}>
        <input
                bind:this={inputElement}
                class="form-control input-{size}"
                type="text"
                id={editorId}
                name={editorName}
                placeholder={placeholder}
                disabled={isDisabled}
                maxlength={validations.maxlength}
                minlength={validations.minlength}
                use:required={validations.required}
                use:formControls={formControl}
                use:barcodeScanner={{formControl, barcodeScannerService, userFeedbackService}}
                use:focusOnMount={isFocused && isScanningSubsystemInitialized}
                use:touched={{formControl}}
                use:ariaErrormessage={formControl}
                use:ariaInvalid={formControl}
                use:ariaDescribedby={inputDescription}
                use:style={{field: formControl.getFieldStateAsStore()}}
                on:barcode-scan={onScan}
                data-main-input/>
        <BarcodeScannerInputControl allowedAutomaticStart={options.allowedAutomaticStart} {isDisabled} {size} {inputElement} {inputGroupElement}>
            <svelte:fragment slot="stop-scanning">
                {localize('stopScanning')}
            </svelte:fragment>
            <svelte:fragment slot="start-scanning">
                {localize('scanWithCamera')}
            </svelte:fragment>
        </BarcodeScannerInputControl>

    </div>
    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>
    <Description {inputDescription}/>
</template>

<style lang="less">
    // input element in read only mode for camera scanner values
    // shows clickable cursor for better understanding
    input {
        &:read-only {
            cursor: pointer;
        }
        &:-moz-read-only {
            cursor: pointer; // same as ^, but for mozzila browsers
        }
    }
</style>